/**
 * Bridges ad click IDs (gclid and friends) from the landing URL to the
 * `create_users_item` call, so a paid click can be joined to the account
 * it produced.
 *
 * WHY: signup starts on attorneyshare.com and finishes here, on a different
 * origin. Google Ads' browser tag only ever sees the CTA *click* on the
 * marketing site -- it never learns which click became an account. The
 * landing page forwards the click ID as a query param; this module catches
 * it at boot and holds it until account creation. Without it, paid spend
 * cannot be attributed and Offline Conversion Import is impossible.
 *
 * Storage is needed rather than plain memory because `handleCreateOrg` runs
 * `tokenManager.destroy()` + `sessionStorageService.clear()` immediately
 * before `createAccount`, and the user may reload mid-flow.
 *
 * ENG-1955.
 */

// NOTE: this exact string is also listed in
// `services/SessionStorage/SessionStorageService.ts` under
// CROSS_AUTH_PRESERVED_RAW_KEYS so it survives the localStorage wipe in
// handleCreateOrg / useVerification. If you rename this, update that list.
const STORAGE_KEY = 'attorneyshare:signup-attribution'

/** 90 days: matches the Google Ads conversion window and the click_view API
 *  lookback, so anything stored here is still joinable on the Ads side. */
const TTL_MS = 90 * 24 * 60 * 60 * 1000

/**
 * Ad-platform click identifiers. `wbraid`/`gbraid` are Google's iOS
 * privacy-safe variants and arrive *instead of* `gclid` on app<->web
 * journeys -- dropping them would silently lose iOS traffic, which matters
 * because ~74% of paid clicks are mobile.
 */
const CLICK_ID_PARAMS = [
  'gclid',
  'wbraid',
  'gbraid',
  'fbclid',
  'msclkid',
] as const

/**
 * The UTM names the warehouse extracts, enumerated -- not a `utm_*` prefix
 * match.
 *
 * A prefix rule admits anything an inbound link invents -- `utm_email`,
 * `utm_token` -- which would then be persisted for 90 days and copied onto
 * the user record. CROSS_AUTH_PRESERVED_RAW_KEYS states that nothing in it
 * may hold a token or PII, and a prefix match cannot honour that promise
 * because the set of keys is not ours to define.
 *
 * The cost of enumerating is that this list and `stg_marketplace__users` in
 * the etl project must be changed together: a name here that the warehouse
 * does not extract is dropped on arrival, and a name there that is missing
 * here is a permanently null column. `utm_id` was missed exactly that way.
 */
const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  // utm_id carries {campaignid} when a Google Ads final-URL suffix is set. It
  // is extracted by stg_marketplace__users and published on
  // fct_organization_attribution, so omitting it here leaves a permanently
  // null column and silently breaks per-campaign attribution the day a
  // suffix is configured.
  'utm_id',
] as const

/** Long enough for any real campaign name, short enough to bound the payload. */
const MAX_VALUE_LENGTH = 200

export type AttributionParams = Record<string, string>

type StoredAttribution = {
  params: AttributionParams
  ts: number
}

const isClickId = (key: string): boolean =>
  (CLICK_ID_PARAMS as readonly string[]).includes(key)

const isAttributionParam = (key: string): boolean =>
  isClickId(key) || (UTM_PARAMS as readonly string[]).includes(key)

/** Values must be plain, bounded strings -- this ends up on a user record. */
const isUsableValue = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.length > 0 &&
  value.length <= MAX_VALUE_LENGTH

/** Keep only well-formed string entries; drop anything else silently. */
const sanitize = (params: Record<string, unknown>): AttributionParams => {
  const clean: AttributionParams = {}
  Object.entries(params).forEach(([key, value]) => {
    if (isAttributionParam(key) && isUsableValue(value)) clean[key] = value
  })
  return clean
}

const read = (): AttributionParams => {
  try {
    // eslint-disable-next-line no-restricted-properties
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as StoredAttribution
    if (!parsed?.params || typeof parsed.ts !== 'number') return {}
    if (typeof parsed.params !== 'object' || Array.isArray(parsed.params))
      return {}
    // Expire rather than forward a stale click ID: attributing a signup to
    // an ad click from four months ago is worse than not attributing it.
    if (Date.now() - parsed.ts > TTL_MS) {
      // eslint-disable-next-line no-restricted-properties
      window.localStorage.removeItem(STORAGE_KEY)
      return {}
    }
    // Re-validate on read: storage is writable by anything running on this
    // origin, and the shape may predate a change to the allowlist.
    return sanitize(parsed.params)
  } catch {
    // localStorage unavailable (private mode / quota) or corrupted JSON.
    return {}
  }
}

const write = (params: AttributionParams) => {
  try {
    const payload: StoredAttribution = { params, ts: Date.now() }
    // eslint-disable-next-line no-restricted-properties
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // Non-fatal: signup must never break because attribution could not
    // be stored.
  }
}

/** Attribution params on a query string, keys lowercased. */
export const parseAttribution = (search: string): AttributionParams => {
  const found: AttributionParams = {}
  new URLSearchParams(search).forEach((value, key) => {
    // Lowercase the key: a hand-built link using GCLID or UTM_Source would
    // otherwise be stored with that casing and missed by downstream parsing.
    const k = key.toLowerCase()
    if (isAttributionParam(k) && isUsableValue(value)) found[k] = value
  })
  return found
}

/**
 * Capture attribution from the current URL. Called once at boot.
 *
 * Merges over what is stored so a later visit carrying only `utm_source`
 * does not wipe an earlier `gclid`, while a fresh click ID does replace the
 * old one -- last-touch, because Offline Conversion Import must be given
 * the gclid Google issued for the click it is crediting.
 */
export const captureAttribution = (search = window.location.search) => {
  const fromUrl = parseAttribution(search)
  if (Object.keys(fromUrl).length === 0) return

  const stored = read()

  // A visit carries at most one authoritative click ID. If a new one arrives,
  // drop every previously-stored click ID rather than merging: Offline
  // Conversion Import credits a single identifier, and leaving a stale gclid
  // beside a fresh wbraid would let the wrong one be uploaded. UTMs still
  // merge, since they describe the visit rather than identify the click.
  const incomingClickId = Object.keys(fromUrl).some(isClickId)
  if (incomingClickId) {
    ;(CLICK_ID_PARAMS as readonly string[]).forEach(k => delete stored[k])
  }

  write({ ...stored, ...fromUrl })
}

/** Everything captured, for merging into the `tags` payload at signup. */
export const readAttribution = (): AttributionParams => read()

export const clearAttribution = () => {
  try {
    // eslint-disable-next-line no-restricted-properties
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // no-op
  }
}
